/**
 * TabHub Styles - Unified theme system for hub pages
 * Used by Financial Hub, Inventory Hub, and future hubs
 */
import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeIn = keyframes `
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroHeaderTop = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin: 0 !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;
export const HeroActions = styled.div `
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1600px;
  margin: 0 auto;
  padding: 32px 24px 48px;

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;
// ============================================
// TAB NAVIGATION
// ============================================
export const TabNavigation = styled.div `
  display: grid;
  grid-template-columns: repeat(${props => Math.min(props.$tabCount, 7)}, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(${props => Math.min(props.$tabCount, 4)}, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const TabButton = styled.button `
  background: ${props => props.$active ? 'var(--bg-primary)' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  padding: 24px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: ${props => props.$active ? 1 : 0};
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);

    &::before {
      opacity: 1;
    }
  }

  ${props => props.$active && `
    &::after {
      content: '✓';
      position: absolute;
      top: 10px;
      right: 10px;
      background: var(--accent-gold, #B8860B);
      color: white;
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
    }
  `}

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;
export const TabIcon = styled.div `
  font-size: 28px;
  margin-bottom: 10px;
  color: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--text-secondary)'};
  transition: color 0.3s;

  ${TabButton}:hover & {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 8px;
  }
`;
export const TabLabel = styled.div `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--text-primary)'};
  margin-bottom: 4px;
  transition: color 0.3s;

  ${TabButton}:hover & {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;
export const TabDescription = styled.div `
  font-size: 12px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 10px;
  }

  @media (max-width: 375px) {
    display: none;
  }
`;
// ============================================
// TAB CONTENT
// ============================================
export const TabContentContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 32px;
  min-height: 600px;
  position: relative;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
    min-height: 400px;
  }
`;
export const TabContentInner = styled.div `
  animation: ${fadeIn} 0.3s ease-in-out;
`;
// ============================================
// INFO BANNER (for mock mode, etc.)
// ============================================
export const InfoBannerWrapper = styled.div `
  margin-bottom: 24px;
`;
